import { beginMessage } from '../message';
import { MessageTag } from '../messageTags';
import type { BinaryReader } from '../BinaryReader';

/**
 * Account-creation / recovery codec. Mirrors the Unity client exactly:
 *  - CheckParent(27): the public (non-agent-build) register gate — validate a referral code.
 *      (Register_code.cs:99-104 checkAgent → packet.Put(_AgentCode.text))
 *  - Register(4): submit the full RegisterModel (RegisterModel.cs Serialize order).
 *  - ForgotPassword(5): request a password reset by email (LoadLogin.cs:126-132 Forgot).
 *
 * All three reuse existing opcodes — no protocol/MessageTag change. Their *failure* replies arrive as
 * MessageTag.SendMessage(0) (a popup string), already handled by the global handler in AppContext.
 */

/** C2S CheckParent(27): the referral/agent code typed into «کد معرف». */
export function encodeCheckParent(referralCode: string): Uint8Array {
  return beginMessage(MessageTag.CheckParent).putString(referralCode).toUint8Array();
}

/** S2C CheckParent(27): the resolved parent (referrer) user id. Register_code.cs:60 reader.GetLong(). */
export function decodeCheckParent(r: BinaryReader): bigint {
  return r.getLong();
}

/** The web's RegisterModel (RegisterModel.cs). parentId comes from a prior CheckParent reply. */
export interface RegisterModel {
  username: string;
  password: string;
  email: string;
  mobile: string;
  parentId: bigint;
  avatarId: number;
}

/**
 * C2S Register(4): RegisterModel serialized as string Username, string Password, string Email,
 * string Mobile, long ParentId, int AvatarId (this exact order — RegisterModel.cs:14-22).
 */
export function encodeRegister(m: RegisterModel): Uint8Array {
  return beginMessage(MessageTag.Register)
    .putString(m.username)
    .putString(m.password)
    .putString(m.email)
    .putString(m.mobile)
    .putLong(m.parentId)
    .putInt(m.avatarId)
    .toUint8Array();
}

/** S2C Register(4): a single bool success flag (Register_code.cs:50). */
export function decodeRegisterResult(r: BinaryReader): boolean {
  return r.getBool();
}

/** C2S ForgotPassword(5): one email string. The reply is a SendMessage(0) status popup. */
export function encodeForgotPassword(email: string): Uint8Array {
  return beginMessage(MessageTag.ForgotPassword).putString(email).toUint8Array();
}
